// Validation rules for a value the page collects — a page variable's
// input control, or a field of a form block. Both run the same rule set
// (the action runner's `validate` step / the form's submit check), so one
// editor serves both.

import { Plus, Trash2 } from 'lucide-react'
import { FIELD_LABEL, INPUT } from './blockEditors'
import type { ValidationRule, ValidationRuleKind } from './types'

const RULES_NEEDING_ARG: Record<ValidationRuleKind, boolean> = {
  required: false, email: false, url: false, number: false,
  regex: true, min_length: true, max_length: true,
}

export function ValidationsEditor({
  rules, onChange,
}: { rules: ValidationRule[]; onChange: (next: ValidationRule[]) => void }) {
  const add = () => onChange([...rules, { rule: 'required' }])
  const upd = (i: number, patch: Partial<ValidationRule>) => {
    const next = [...rules]; next[i] = { ...next[i], ...patch }; onChange(next)
  }
  const remove = (i: number) => onChange(rules.filter((_, k) => k !== i))
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Validations</label>
        <button
          onClick={(e) => { e.stopPropagation(); add() }}
          className="btn btn-ghost btn-sm"
        ><Plus size={13} /> Add</button>
      </div>
      {rules.length === 0 && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          No validations. Add rules that the action runner's <em>validate</em> step will check.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rules.map((r, i) => (
          <div
            key={i}
            style={{
              padding: 8, borderRadius: 6,
              border: '1px solid var(--color-border)',
              background: 'var(--color-bg)',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}
          >
            <div style={{ display: 'flex', gap: 6 }}>
              <select
                value={r.rule}
                onChange={(e) => upd(i, { rule: e.target.value as ValidationRuleKind, arg: undefined })}
                style={{ ...INPUT, flex: 1 }}
              >
                <option value="required">required</option>
                <option value="email">email</option>
                <option value="url">url</option>
                <option value="number">number</option>
                <option value="regex">regex</option>
                <option value="min_length">min_length</option>
                <option value="max_length">max_length</option>
                {!(r.rule in RULES_NEEDING_ARG) && <option value={r.rule}>{r.rule} (unrecognised)</option>}
              </select>
              {RULES_NEEDING_ARG[r.rule] && (
                <input
                  value={r.arg ?? ''}
                  onChange={(e) => upd(i, { arg: e.target.value })}
                  placeholder={r.rule === 'regex' ? 'pattern' : 'length'}
                  style={{ ...INPUT, width: 120 }}
                />
              )}
              <button
                onClick={(e) => { e.stopPropagation(); remove(i) }}
                className="btn btn-ghost btn-sm"
                style={{ padding: 4, color: 'var(--color-error)' }}
                title="Remove"
              ><Trash2 size={13} /></button>
            </div>
            <input
              value={r.message || ''}
              onChange={(e) => upd(i, { message: e.target.value })}
              placeholder="Custom message (optional)"
              style={INPUT}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
